import { CdkMenuTrigger } from '@angular/cdk/menu';
import { Component, computed, effect, inject, input, signal, TemplateRef } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiActionDialogService, ActionDialog } from '@siemens/element-ng/action-modal';
import { SiFormContainerComponent, SiFormModule } from '@siemens/element-ng/form';
import { SiHelpButtonComponent } from '@siemens/element-ng/help-button';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { ModalRef, SiModalService } from '@siemens/element-ng/modal';
import { SiSelectModule } from '@siemens/element-ng/select';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { Observable } from 'rxjs';

import { STANDARD_MODAL_CONFIG } from '../shared/const';
import { WorkspaceMember, Workspace } from '../shared/DataTypes';
import { AccountSummary, UserRole } from '../shared/services/account.service';
import { SettingsService } from '../shared/services/settings.service';
import { ShareDataService } from '../shared/services/share-data.service';
import { WorkspaceService } from '../shared/services/workspace.service';
import { UserSelectSource } from './user-select/user-select-source';
import { UserSelectComponent } from './user-select/user-select.component';

const reservedWorkspaceNameValidator = (control: AbstractControl): ValidationErrors | null => {
  return control.value?.trim().toLowerCase() === 'public' ? { reservedWorkspaceName: true } : null;
};

@Component({
  selector: 'app-workspaces',
  imports: [
    CdkMenuTrigger,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    SiFormModule,
    SiMenuModule,
    SiSelectModule,
    UserSelectComponent,
    SiHelpButtonComponent,
    SiFormContainerComponent
  ],
  templateUrl: './workspaces.component.html',
  styleUrl: './workspaces.component.scss'
})
export class WorkspacesComponent {
  private formBuilder = inject(FormBuilder);
  private actionDialogueService = inject(SiActionDialogService);
  private modalService = inject(SiModalService);
  private translateService = inject(TranslateService);
  private workspaceService = inject(WorkspaceService);
  private toastNotificationService = inject(SiToastNotificationService);
  private shareDataService = inject(ShareDataService);
  private settingsService = inject(SettingsService);

  readonly accountSummary = input.required<AccountSummary>();
  readonly availableWorkspaces = input.required<Workspace[]>();

  constructor() {
    effect(() => {
      // Update available workspaces in ShareDataService whenever it changes here
      const showOtherPrivateWorkspaces = this.showOtherPrivateWorkspaces();
      this.shareDataService.setShowOtherPrivateWorkspaces(showOtherPrivateWorkspaces);

      // If the default workspace is no longer visible due to the change in showOtherPrivateWorkspaces,
      // reset the default workspace to undefined (which will fall back to Public)
      const defaultWorkspaceId = this.settingsService.defaultWorkspaceId();
      const defaultWorkspaceIsVisible = this.availableWorkspaces().some(
        workspace => workspace.id === defaultWorkspaceId
      );
      if (!showOtherPrivateWorkspaces && defaultWorkspaceId && !defaultWorkspaceIsVisible) {
        this.settingsService.setDefaultWorkspaceId(undefined);
      }
    });
  }

  /** Admin option: Display private workspaces of other users */
  readonly showOtherPrivateWorkspaces = signal<boolean>(false);

  // Workspaces prioritized for display,
  // with Public and own Private workspace at the top,
  // followed by the rest in no particular order
  readonly prioritizedWorkspaces = computed(() => {
    const workspaces = this.availableWorkspaces();
    const publicWorkspace = workspaces.find(
      workspace => workspace.name === 'Public' && !workspace.isPrivate
    );
    const ownPrivateWorkspace = workspaces.find(workspace => this.isOwnPrivateWorkspace(workspace));

    return [publicWorkspace, ownPrivateWorkspace]
      .filter((workspace): workspace is Workspace => !!workspace)
      .concat(
        workspaces.filter(
          workspace => workspace !== publicWorkspace && workspace !== ownPrivateWorkspace
        )
      );
  });

  /** Separate source instances so each si-select has its own independent Subject/observable chain. */
  createWorkspaceUserSelectSource = new UserSelectSource();
  manageUsersSelectSource = new UserSelectSource(
    // Filter out users that are already members of the workspace from the options
    () => new Set(this.workspaceMembers().map(member => member.oid))
  );

  /** Selected user OIDs in the create-workspace modal */
  selectedCreateWorkspaceUsers: string[] = [];

  /** Selected user OIDs in the manage-users modal */
  selectedManageUsers: string[] = [];

  /** Members of the workspace currently open in the Manage Users modal */
  readonly workspaceMembers = signal<WorkspaceMember[]>([]);

  // FormGroup for the form
  createNewWorkspaceForm: FormGroup = this.formBuilder.group({
    workspaceName: [
      '',
      [Validators.required, Validators.minLength(2), reservedWorkspaceNameValidator]
    ],
    description: ['', [Validators.maxLength(500)]]
  });

  errorCodeTranslateKeyMap = {
    reservedWorkspaceName: 'WORKSPACE.ERROR_RESERVED_WORKSPACE_NAME'
  };

  showLeaveWorkspaceDialog(workspace: Workspace): void {
    this.showActionDialog({
      type: 'confirmation',
      heading: this.translateService.instant('WORKSPACE.LEAVE_WORKSPACE'),
      message: this.translateService.instant('WORKSPACE.LEAVE_WORKSPACE_MESSAGE', {
        workspace: workspace.name
      })
    }).subscribe((result: any) => {
      if (result === 'confirm') {
        this.workspaceService.leaveWorkspace(workspace.id).subscribe({
          error: e => {
            this.toastNotificationService.showToastNotification({
              state: 'danger',
              title: this.translateService.instant('WORKSPACE.ERROR_LEAVE_WORKSPACE'),
              message: e.statusText
            });
          },
          next: (response: any) => {
            // Update available workspaces to receive the new workspace
            this.workspaceService.getAllWorkspaces().subscribe({
              next: (workspaces: Workspace[]) => {
                this.shareDataService.setAvailableWorkspaces(workspaces);
              }
            });
          },
          complete: () => {
            this.toastNotificationService.showToastNotification({
              state: 'success',
              title: this.translateService.instant('SHARED.SUCCESS'),
              message: this.translateService.instant('WORKSPACE.SUCCESS_LEAVE_WORKSPACE', {
                workspace: workspace.name
              })
            });
          }
        });
      }
    });
  }

  showRemoveUserFromWorkspaceDialog(member: WorkspaceMember, workspace: Workspace): void {
    this.showActionDialog({
      type: 'confirmation',
      heading: this.translateService.instant('WORKSPACE.REMOVE_USER'),
      message: this.translateService.instant('WORKSPACE.REMOVE_USER_MESSAGE', {
        user: member.displayName ?? member.gid,
        workspace: workspace.name
      })
    }).subscribe((result: any) => {
      if (result === 'confirm') {
        if (!member.oid) return;
        this.workspaceService.removeUserFromWorkspace(workspace.id, member.oid).subscribe({
          error: e => {
            this.toastNotificationService.showToastNotification({
              state: 'danger',
              title: this.translateService.instant('WORKSPACE.ERROR_REMOVE_USER'),
              message: e.statusText
            });
          },
          complete: () => {
            this.toastNotificationService.showToastNotification({
              state: 'success',
              title: this.translateService.instant('SHARED.SUCCESS'),
              message: this.translateService.instant('WORKSPACE.SUCCESS_REMOVE_USER')
            });
            this.workspaceService
              .getWorkspaceMembers(workspace.id)
              .subscribe(members => this.workspaceMembers.set(members));
          }
        });
      }
    });
  }

  showDeleteWorkspaceDialog(workspace: Workspace): void {
    this.showActionDialog({
      type: 'confirmation',
      heading: this.translateService.instant('WORKSPACE.DELETE_WORKSPACE'),
      message: this.translateService.instant('WORKSPACE.DELETE_WORKSPACE_MESSAGE', {
        workspace: workspace.name
      })
    }).subscribe(result => {
      if (result === 'confirm') {
        this.workspaceService.deleteWorkspace(workspace.id).subscribe({
          error: e => {
            this.toastNotificationService.showToastNotification({
              state: 'danger',
              title: this.translateService.instant('WORKSPACE.ERROR_DELETING_WORKSPACE'),
              message: e.statusText
            });
          },
          next: (response: any) => {
            // Update available workspaces to receive the new workspace
            this.workspaceService.getAllWorkspaces().subscribe({
              next: (workspaces: Workspace[]) => {
                this.shareDataService.setAvailableWorkspaces(workspaces);
              }
            });
          },
          complete: () => {
            this.toastNotificationService.showToastNotification({
              state: 'success',
              title: this.translateService.instant('SHARED.SUCCESS'),
              message: this.translateService.instant('WORKSPACE.SUCCESS_DELETE_WORKSPACE', {
                workspace: workspace.name
              })
            });
          }
        });
      }
    });
  }

  showActionDialog(dialog: ActionDialog): Observable<any> {
    return this.actionDialogueService.showActionDialog(dialog);
  }

  openModal(template: TemplateRef<any>, workspace?: Workspace): void {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      inputValues: {
        ws: workspace
      }
    });
  }

  onCreateNewWorkspace(modalRef?: ModalRef): void {
    if (this.createNewWorkspaceForm.valid && this.selectedCreateWorkspaceUsers.length > 0) {
      const formData = this.createNewWorkspaceForm.value;
      formData.userOidList = this.selectedCreateWorkspaceUsers;
      this.workspaceService.createNewWorkspace(formData).subscribe({
        error: e => {
          this.toastNotificationService.showToastNotification({
            state: 'danger',
            title: 'Error creating Workspace',
            message: e.statusText
          });
        },
        next: (response: any) => {
          // Update available workspaces to receive the new workspace
          this.workspaceService.getAllWorkspaces().subscribe({
            next: (workspaces: Workspace[]) => {
              this.shareDataService.setAvailableWorkspaces(workspaces);
            }
          });
        },
        complete: () => {
          this.toastNotificationService.showToastNotification({
            state: 'success',
            title: 'Success',
            message: 'Workspace ' + formData.workspaceName + ' created successfully'
          });
          // Close modal and reset form after successful creation
          if (modalRef) {
            this.closeModal(modalRef);
          }
          this.createNewWorkspaceForm.reset();
          this.selectedCreateWorkspaceUsers = [];
        }
      });
    } else {
      console.log('Form is invalid or no users selected');
    }
  }

  /** Open the Manage Users modal, loading members first. */
  openManageUsersModal(template: TemplateRef<any>, workspace: Workspace): void {
    this.selectedManageUsers = [];
    this.workspaceService.getWorkspaceMembers(workspace.id).subscribe({
      next: members => {
        this.workspaceMembers.set(members);
        this.openModal(template, workspace);
      }
    });
  }

  /** True when logged in user is the creator of the workspace. */
  isCurrentUserCreator(workspace: Workspace): boolean {
    return workspace.creatorGid === this.accountSummary()?.gid;
  }

  isAdmin(): boolean {
    return this.accountSummary().roles.includes(UserRole.ADMIN);
  }

  isUneditableWorkspace(workspace: Workspace): boolean {
    return workspace.name === 'Public' || workspace.isPrivate;
  }

  canManageWorkspace(workspace: Workspace): boolean {
    return this.isAdmin() || !this.isUneditableWorkspace(workspace);
  }

  canLeaveWorkspace(workspace: Workspace): boolean {
    if (this.isUneditableWorkspace(workspace)) {
      return false; // Public workspace cannot be left
    }
    // Normal users only get shown workspaces they are a member of,
    // so if it's not Public Siemens Workspace, they can leave it
    // Admins can see all workspaces, but can only leave ones they are part of
    return workspace.adminInfo?.partOfWs ?? false;
  }

  onAddUsersToManagedWorkspace(workspace: Workspace): void {
    const userOids = this.selectedManageUsers;
    if (!userOids || userOids.length === 0) {
      console.warn('No users selected to add to workspace, this should not happen');
      return;
    }

    this.workspaceService.addUserToWorkspace(userOids, workspace.id).subscribe({
      error: e => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: 'Error adding Users',
          message: e.statusText
        });
      },
      next: () => {},
      complete: () => {
        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: 'Success',
          message: 'Users added successfully to Workspace ' + workspace.name
        });
        this.selectedManageUsers = [];
        this.workspaceService
          .getWorkspaceMembers(workspace.id)
          .subscribe(members => this.workspaceMembers.set(members));
      }
    });
  }

  closeModal(modalRef: ModalRef): void {
    modalRef.hide();
  }

  isInvalid(controlName: string): boolean {
    const control = this.createNewWorkspaceForm.get(controlName);
    return !!control?.invalid && (!!control?.dirty || !!control?.touched);
  }

  isOwnPrivateWorkspace(workspace: Workspace): boolean {
    return workspace.isPrivate && workspace.creatorGid === this.accountSummary()?.gid;
  }

  isPinnedWorkspace(workspace: Workspace): boolean {
    const isPublicWorkspace = workspace.name === 'Public' && !workspace.isPrivate;
    return isPublicWorkspace || this.isOwnPrivateWorkspace(workspace);
  }

  readonly defaultWorkspace = computed(() =>
    this.settingsService.resolveDefaultWorkspace(this.availableWorkspaces())
  );

  isDefaultWorkspace(workspace: Workspace): boolean {
    return this.defaultWorkspace()?.id === workspace.id;
  }

  /** Public is the fallback default, so unselecting it would have no effect. */
  canToggleDefaultWorkspace(workspace: Workspace): boolean {
    return !this.isDefaultWorkspace(workspace) || workspace.name !== 'Public';
  }

  toggleDefaultWorkspace(workspace: Workspace): void {
    this.settingsService.setDefaultWorkspaceId(
      this.isDefaultWorkspace(workspace) ? undefined : workspace.id
    );
  }

  hasCustomWorkspaces(): boolean {
    return this.availableWorkspaces().some(
      workspace =>
        !(workspace.name === 'Public' && workspace.isPrivate === false) &&
        !this.isOwnPrivateWorkspace(workspace)
    );
  }
}
